import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {Chip, Container} from '@mui/material';
import Box from '@mui/material/Box';
import MUILink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, {useCallback, useEffect} from 'react';
import {useParams} from 'react-router';
import {
  canCreatePublication,
  canEditPublication,
  canViewAttachment,
} from '../auth.js';
import {useDownload, usePermissions, usePublication} from '../client.js';
import Link from '../components/Link.jsx';
import {formatDate, getNewSearchUrl} from '../utils.js';
import Footer from './Footer.jsx';
import Page, {editLink, newLink} from './Page.jsx';
import PublicationAbstract from './PublicationAbstract.jsx';
import PublicationAuthors from './PublicationAuthor.jsx';
import PublicationDataTypes from './PublicationDataTypes.jsx';
import PublicationKeywords from './PublicationKeywords.jsx';
import RelatedLinks from './RelatedLinks.jsx';

function Publication() {
  const {id} = useParams();
  const {data} = usePublication(id);
  const {data: permissions} = usePermissions();
  const alternateLink = data?.links.filter(link => link.rel === 'alternate')[0];
  const attachmentLink = data?.links.filter(
    link => link.rel === 'attachment',
  )[0];

  const {request: download, data: attachment} = useDownload(
    attachmentLink?.href,
  );

  useEffect(() => {
    if (!attachment) {
      return;
    }
    const fileUrl = window.URL.createObjectURL(attachment);
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = `${data.title}.pdf`;
    anchor.click();
  }, [attachment, data]);

  const onAttachmentClick = useCallback(() => {
    download();
  }, [download]);

  const links = [];
  if (canEditPublication(permissions, data)) {
    links.push(editLink);
  }
  if (canCreatePublication(permissions, data)) {
    links.push(newLink);
  }

  return (
    <Page links={[links]} withFooter={false}>
      {data && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: `calc(100vh - 94px)`,
          }}
        >
          <Box sx={{flexGrow: 1}}>
            <Box
              sx={{
                width: {xs: '100%', sm: 'auto'},
                textAlign: 'center',
                marginBottom: 1,
                marginTop: 3,
              }}
            >
              <Typography variant="subtitle1">
                {data.publisher}, {formatDate(data.datePublished)}
              </Typography>
            </Box>

            <Box sx={{textAlign: 'center', marginBottom: 3}}>
              <Typography
                variant="h5"
                sx={{margin: '0 auto', maxWidth: {lg: '70%', sm: 'auto'}}}
              >
                {!data.published && (
                  <Chip
                    label="draft"
                    size="small"
                    sx={{
                      marginRight: 1,
                      marginBottom: 0.5,
                      fontVariant: 'all-small-caps',
                    }}
                  />
                )}
                {data.title}{' '}
                {attachmentLink && canViewAttachment(permissions) && (
                  <Tooltip title="download publication">
                    <MUILink
                      onClick={onAttachmentClick}
                      sx={{cursor: 'pointer'}}
                    >
                      <FileDownloadOutlinedIcon
                        sx={{marginLeft: 1, marginBottom: -0.5}}
                      />
                    </MUILink>
                  </Tooltip>
                )}
                <Tooltip title="view online">
                  <MUILink
                    href={alternateLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon sx={{marginLeft: 1, marginBottom: -0.5}} />
                  </MUILink>
                </Tooltip>
              </Typography>
              <Box
                sx={{display: 'flex', justifyContent: 'center', gap: 2, mt: 1}}
              >
                {data.type && (
                  <Chip
                    label={data.type.toUpperCase()}
                    component={Link}
                    to={getNewSearchUrl({type: data.type})}
                    clickable
                    size="small"
                    sx={{
                      fontSize: '11px',
                    }}
                  />
                )}
                {data.topics.map(topic => (
                  <Chip
                    key={topic}
                    label={topic.toUpperCase()}
                    component={Link}
                    to={getNewSearchUrl({topic})}
                    clickable
                    size="small"
                    sx={{
                      fontSize: '11px',
                    }}
                  />
                ))}
              </Box>
            </Box>

            <PublicationAbstract abstract={data.abstract} />
            <PublicationAuthors authors={data.authors} />
            <RelatedLinks links={data?.links} />
            <PublicationDataTypes dataTypes={data.dataTypes} />
            <PublicationKeywords keywords={data.keywords} />
          </Box>

          <Container disableGutters>
            <Footer />
          </Container>
        </Box>
      )}
    </Page>
  );
}

export default Publication;
